import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch, cachedGet } from '../../lib/api-client';
import { Spinner } from '../../components/kit/Spinner';
import { ErrorState } from '../../components/kit/ErrorState';
import { EmptyState } from '../../components/kit/EmptyState';
import { Card } from '../../components/kit/Card';
import { Button } from '../../components/kit/Button';
import { StatusBadge } from '../../components/kit/StatusBadge';
import { TripCard } from '../../components/home/TripCard';
import { formatISTDate } from '../../lib/format';
import { haptic } from '../../lib/haptic';

type UpcomingTrip = {
  id: string;
  code?: string | null;
  pickupTime: string;
  pickupLocation?: string | null;
  dropLocation?: string | null;
  status: string;
  client?: { name: string } | null;
  isVip?: boolean;
};

type HistoryItem = {
  id: string;
  code?: string | null;
  completedAt: string;
  status: string;
  client?: { name: string } | null;
  pickupLocation?: string | null;
  dropLocation?: string | null;
};

type HistoryResponse = { items: HistoryItem[]; nextCursor: string | null };

type Tab = 'upcoming' | 'completed';

export function HistoryRoute() {
  const { t } = useTranslation('trip');
  const [tab, setTab] = useState<Tab>('upcoming');

  // Upcoming tab state (matches HomeRoute — same endpoint + cache layer)
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [upcomingError, setUpcomingError] = useState(false);
  const [upcoming, setUpcoming] = useState<UpcomingTrip[]>([]);

  // Completed tab state
  const [completedLoading, setCompletedLoading] = useState(true);
  const [completedLoadingMore, setCompletedLoadingMore] = useState(false);
  const [completedError, setCompletedError] = useState(false);
  const [completed, setCompleted] = useState<HistoryItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);

  const loadUpcoming = async () => {
    setUpcomingError(false);
    setUpcomingLoading(true);
    try {
      const result = await cachedGet<{ items: UpcomingTrip[] }>('/driver-app/me/trips/today');
      setUpcoming(result.data.items);
    } catch {
      setUpcomingError(true);
    } finally {
      setUpcomingLoading(false);
    }
  };

  const loadCompletedInitial = async () => {
    setCompletedError(false);
    setCompletedLoading(true);
    try {
      const res = await apiFetch<HistoryResponse>('/driver-app/me/trips/history?limit=20');
      setCompleted(res.items);
      setCursor(res.nextCursor);
    } catch {
      setCompletedError(true);
    } finally {
      setCompletedLoading(false);
    }
  };

  const loadCompletedMore = async () => {
    if (!cursor) return;
    setCompletedLoadingMore(true);
    try {
      const res = await apiFetch<HistoryResponse>(
        `/driver-app/me/trips/history?limit=20&cursor=${encodeURIComponent(cursor)}`,
      );
      setCompleted(prev => [...prev, ...res.items]);
      setCursor(res.nextCursor);
    } catch {
      // Soft fail; user can tap again
    } finally {
      setCompletedLoadingMore(false);
    }
  };

  useEffect(() => {
    void loadUpcoming();
    void loadCompletedInitial();
  }, []);

  return (
    <div className="px-5 pb-10">
      {/* Sticky segmented tabs — pinned to the top of the AppShell scroll area
          (<main overflow-y-auto>) so only the list below scrolls. -mx-5 px-5
          spans the bg full-width; bg-page hides the list scrolling beneath. */}
      <div className="sticky top-0 z-20 -mx-5 px-5 pt-2 pb-3 bg-[var(--color-bg-page)]">
        <div
          role="tablist"
          aria-label={t('history')}
          className="grid grid-cols-2 gap-1 p-1 rounded-[12px] border border-[var(--color-border)]"
          style={{ background: 'rgba(0, 0, 0, 0.035)' }}
        >
          <TabButton active={tab === 'upcoming'} onClick={() => { if (tab !== 'upcoming') { haptic.tap(); setTab('upcoming'); } }}>
            {t('tabs.upcoming')}
          </TabButton>
          <TabButton active={tab === 'completed'} onClick={() => { if (tab !== 'completed') { haptic.tap(); setTab('completed'); } }}>
            {t('tabs.completed')}
          </TabButton>
        </div>
      </div>

      <div className="mt-2">
        {tab === 'upcoming' ? (
          <UpcomingPanel
            loading={upcomingLoading}
            error={upcomingError}
            trips={upcoming}
            onRetry={loadUpcoming}
            emptyTitle={t('empty.upcoming')}
          />
        ) : (
          <CompletedPanel
            loading={completedLoading}
            loadingMore={completedLoadingMore}
            error={completedError}
            items={completed}
            cursor={cursor}
            onRetry={loadCompletedInitial}
            onLoadMore={loadCompletedMore}
            emptyTitle={t('empty.completed')}
            loadMoreLabel={t('loadMore')}
          />
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className="inline-flex items-center justify-center h-[38px] rounded-[9px] text-sm transition-colors"
      style={{
        background: active ? 'var(--color-surface)' : 'transparent',
        color: active ? 'var(--color-text)' : 'var(--color-text-secondary)',
        fontWeight: active ? 700 : 600,
        boxShadow: active
          ? '0 1px 3px rgba(15, 59, 38, 0.08), 0 1px 2px rgba(15, 59, 38, 0.04)'
          : 'none',
      }}
    >
      {children}
    </button>
  );
}

function UpcomingPanel({
  loading,
  error,
  trips,
  onRetry,
  emptyTitle,
}: {
  loading: boolean;
  error: boolean;
  trips: UpcomingTrip[];
  onRetry: () => void;
  emptyTitle: string;
}) {
  if (loading && trips.length === 0) {
    return <div className="flex justify-center p-8"><Spinner size={32} /></div>;
  }
  if (error && trips.length === 0) return <ErrorState onRetry={onRetry} />;
  if (trips.length === 0) return <EmptyState title={emptyTitle} />;

  return (
    <ul className="space-y-3">
      {trips.map((trip, idx) => (
        <TripCard key={trip.id} trip={trip} isNext={idx === 0} accent={idx === 0} />
      ))}
    </ul>
  );
}

function CompletedPanel({
  loading,
  loadingMore,
  error,
  items,
  cursor,
  onRetry,
  onLoadMore,
  emptyTitle,
  loadMoreLabel,
}: {
  loading: boolean;
  loadingMore: boolean;
  error: boolean;
  items: HistoryItem[];
  cursor: string | null;
  onRetry: () => void;
  onLoadMore: () => void;
  emptyTitle: string;
  loadMoreLabel: string;
}) {
  if (loading && items.length === 0) {
    return <div className="flex justify-center p-8"><Spinner size={32} /></div>;
  }
  if (error && items.length === 0) return <ErrorState onRetry={onRetry} />;
  if (items.length === 0) return <EmptyState title={emptyTitle} />;

  // Group by completion date so we can render Fraunces day-number headers,
  // matching the v2 history mockup. Map preserves insertion order, so groups
  // appear in the same DESC order the backend already returns the items in.
  const groups = new Map<string, HistoryItem[]>();
  for (const it of items) {
    const key = (it.completedAt || '').slice(0, 10); // YYYY-MM-DD
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(it);
  }

  return (
    <>
      <div className="space-y-6">
        {Array.from(groups.entries()).map(([dateKey, dayItems]) => {
          const date = dateKey ? new Date(dateKey) : null;
          const day = date ? new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric' }).format(date) : '—';
          const weekdayMonth = date
            ? new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', month: 'short' }).format(date)
            : '';
          return (
            <section key={dateKey}>
              <div className="flex items-baseline gap-2 mb-2 px-1">
                <span
                  className="serif-accent leading-none"
                  style={{ fontSize: 30, color: 'var(--color-text)' }}
                >
                  {day}
                </span>
                <span
                  className="font-bold uppercase"
                  style={{ fontSize: 11, color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.10em' }}
                >
                  {weekdayMonth}
                  <span style={{ marginLeft: 8, opacity: 0.7 }}>· {dayItems.length}</span>
                </span>
              </div>
              <ul className="space-y-3">
                {dayItems.map(it => (
                  <li key={it.id}>
                    <Link to={`/trips/${it.code || it.id}`} className="block">
                      <Card>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-[var(--color-text-secondary)]">
                            {formatISTDate(it.completedAt)}
                          </span>
                          <StatusBadge status={it.status} />
                        </div>
                        <div className="text-base font-semibold text-[var(--color-text)]">
                          {it.client?.name || '—'}
                        </div>
                        {it.pickupLocation || it.dropLocation ? (
                          <div className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] mt-1.5 min-w-0">
                            {it.pickupLocation ? (
                              <span className="flex-1 min-w-0 truncate">{it.pickupLocation}</span>
                            ) : null}
                            {it.pickupLocation && it.dropLocation ? (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                                style={{ color: 'var(--color-text-dim, #9AA3AC)', flexShrink: 0 }}
                              >
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                              </svg>
                            ) : null}
                            {it.dropLocation ? (
                              <span className="flex-1 min-w-0 truncate">{it.dropLocation}</span>
                            ) : null}
                          </div>
                        ) : null}
                      </Card>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
      {cursor ? (
        <div className="mt-6">
          <Button variant="secondary" onClick={() => { haptic.tap(); onLoadMore(); }} loading={loadingMore}>
            {loadMoreLabel}
          </Button>
        </div>
      ) : null}
    </>
  );
}
